// src/components/farm/FarmProductGrid.js
// Infinite-scroll product grid for the Farm marketplace.
// Responsive: 2 cols mobile → 3 cols tablet → 4 cols desktop.
import React, { useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import FarmProductCard from './FarmProductCard';

/**
 * @param {{
 *   products: object[],
 *   isWished: (id: string) => boolean,
 *   wishCounts: Record<string, number>,
 *   onToggleWishlist: (product: object) => void,
 *   onReportProduct?: (product: object) => void,
 *   hasMore: boolean,
 *   loadMore: () => void,
 *   loading: boolean,
 * }} props
 */
export default function FarmProductGrid({
  products,
  isWished,
  wishCounts = {},
  onToggleWishlist,
  onReportProduct,
  hasMore,
  loadMore,
  loading,
}) {
  const { t } = useTranslation('farmMarketplace');
  const sentinelRef = useRef(null);

  // Intersection Observer for infinite scroll
  const observerCallback = useCallback(
    (entries) => {
      if (entries[0]?.isIntersecting && hasMore && !loading) {
        loadMore();
      }
    },
    [hasMore, loadMore, loading]
  );

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(observerCallback, {
      rootMargin: '200px',
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [observerCallback]);

  if (!products.length && !loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center px-4">
        <img
          src="/images/empty-products.svg"
          alt=""
          className="w-32 mb-4 opacity-60"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
        <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-200">
          {t('noProducts')}
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-xs">
          {t('noProductsDesc')}
        </p>
      </div>
    );
  }

  return (
    <>
      <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 sm:gap-3 lg:gap-4">
        {products.map((product) => (
          <FarmProductCard
            key={product.id}
            product={product}
            isWished={isWished(product.id)}
            wishCount={wishCounts[product.id] ?? product.wishCount ?? 0}
            onToggleWishlist={() => onToggleWishlist(product)}
            onReport={onReportProduct}
          />
        ))}
      </section>

      {/* Infinite scroll sentinel */}
      <div ref={sentinelRef} className="h-8" aria-hidden="true" />

      {/* Loading indicator for next page */}
      {loading && products.length > 0 && (
        <div className="flex justify-center py-6">
          <div className="orrith-loader orrith-loader-sm orrith-loader-primary" />
        </div>
      )}
    </>
  );
}
